// Extracted to @heidi/session — history: git log --follow src/atoms/sessionAtoms.tsx
import {
  initialNativeBackupUploadGate,
  type NativeBackupUploadGate,
} from '@/atoms/nativeBackupUploadGate';
import { atomWithHash } from '@heidi/utils/jotai-helpers';
import { atom } from 'jotai';

export const shouldShowSocketErrorModal = atom<boolean>(false);

export const shouldShowAudioDeviceChangedAutomaticallyModal = atomWithHash<boolean>(
  'audioDeviceChangedAutomatically',
  false,
  {
    setHash: 'replaceState',
  }
);

export const shouldShowAudioNoSoundDetectedModal = atom<boolean>(false);

export const errorStartingRecordingAtom = atom<{
  isOpen: boolean;
  error?: string;
  shouldShowTroubleshootingGuide?: boolean;
}>({
  isOpen: false,
});

/**
 * Which upload prompt is open, keyed by session so it follows its session. One
 * union, not two atoms: a legacy FE writer and the native recovery listener
 * cannot leave a stale half behind.
 */
export type BackupUploadPrompt =
  /** `partial`: the recording ended early, so a note covers only part of it. */
  | { kind: 'frontendUpload'; sessionId: string; partial?: boolean }
  | { kind: 'nativeAbandoned'; sessionId: string; recordingId: string }
  /** `uploadFailed`: the backup upload failed while this prompt was open. */
  | { kind: 'captureTruncated'; sessionId: string; uploadFailed?: boolean }
  /** The recording had no usable audio: names the mic and offers another. */
  | { kind: 'silentCapture'; sessionId: string; micLabel?: string }
  | null;

export const backupUploadPromptAtom = atom<BackupUploadPrompt>(null);

type LegacyBackupUploadUpdate =
  | string
  | null
  | ((current: string | null) => string | null);

/**
 * Legacy `string | null` view of `backupUploadPromptAtom`: reads give the
 * prompted session id, a session id raises an FE-upload prompt, `null` closes
 * the open one. Writing the already-prompted id is a no-op, so a legacy clear
 * never downgrades a native prompt.
 */
export const requireBackupUploadModalAtom = atom(
  (get) => get(backupUploadPromptAtom)?.sessionId ?? null,
  (get, set, update: LegacyBackupUploadUpdate) => {
    const current = get(backupUploadPromptAtom);
    const currentSessionId = current?.sessionId ?? null;
    const next = typeof update === 'function' ? update(currentSessionId) : update;
    if (next === currentSessionId) return;
    set(
      backupUploadPromptAtom,
      next === null ? null : { kind: 'frontendUpload', sessionId: next }
    );
  }
);

/**
 * The modal's gate for a backup still uploading natively (`backupPendingAsync`).
 * No other signal shows that upload (no FE mutation, status stays NOT_STARTED),
 * so the stop writes it as a positive fact. It is a small state machine; read
 * and write it only through the reducers in `@/atoms/nativeBackupUploadGate`.
 */
export const nativeBackupUploadGateAtom = atom<NativeBackupUploadGate>(
  initialNativeBackupUploadGate
);

/** True for the truncated-capture prompt of this session. */
export const isCaptureTruncatedPromptFor = (
  prompt: BackupUploadPrompt,
  sessionId: string
): prompt is Extract<NonNullable<BackupUploadPrompt>, { kind: 'captureTruncated' }> =>
  prompt?.kind === 'captureTruncated' && prompt.sessionId === sessionId;
